import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { kanbanApiService } from '../../services/kanbanApi';
import {
  KANBAN_CARD_TYPES,
  KANBAN_CLASSES_OF_SERVICE,
  CARD_TYPE_CONFIG,
  CLASS_OF_SERVICE_CONFIG,
  type KanbanList,
  type KanbanSwimlane,
  type KanbanLabel,
  type KanbanCardType,
  type KanbanClassOfService,
} from '../../types/kanban';
import { cn } from '../../utils/cn';

interface CreateCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  boardId: string;
  lists: KanbanList[];
  swimlanes: KanbanSwimlane[];
  labels: KanbanLabel[];
  onCreated: () => void;
}

export const CreateCardModal = ({
  isOpen,
  onClose,
  boardId,
  lists,
  swimlanes,
  labels: _labels,
  onCreated,
}: CreateCardModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [listId, setListId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState(5);
  const [cardType, setCardType] = useState<KanbanCardType>('task');
  const [classOfService, setClassOfService] = useState<KanbanClassOfService>('standard');
  const [dueDate, setDueDate] = useState('');
  const [swimlaneId, setSwimlaneId] = useState('');
  const [coverColor, setCoverColor] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setIsSubmitting(false);
    setTitle('');
    setDescription('');
    setPriority(5);
    setCardType('task');
    setClassOfService('standard');
    setDueDate('');
    setSwimlaneId('');
    setCoverColor('');
    setListId(lists[0]?.id ?? '');
  }, [isOpen, lists]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      setError('Title is required.');
      return;
    }
    if (!listId) {
      setError('Select a list.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    let dueMs: number | null = null;
    if (dueDate) {
      const parsed = new Date(`${dueDate}T00:00:00`).getTime();
      dueMs = Number.isNaN(parsed) ? null : parsed;
    }

    try {
      await kanbanApiService.createCard(boardId, {
        list_id: listId,
        title: trimmed,
        description: description.trim() || undefined,
        priority,
        card_type: cardType,
        class_of_service: classOfService,
        due_date: dueMs,
        swimlane_id: swimlaneId || null,
        cover_color: coverColor.trim() || null,
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create card.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999] overflow-y-auto">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
      >
        <button
          type="button"
          onClick={onClose}
          className="modal-close z-10"
          aria-label="Close"
          disabled={isSubmitting}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="modal-header">
          <h2 className="modal-title">New card</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <span className="sr-only">{_labels.length} labels defined for this board</span>
          {error && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label htmlFor="create-card-list" className="text-xs font-medium text-glass-400 mb-1.5 block">
              List
            </label>
            <select
              id="create-card-list"
              value={listId}
              onChange={(e) => setListId(e.target.value)}
              className="glass-input w-full"
              disabled={isSubmitting || lists.length === 0}
              required
            >
              {lists.length === 0 ? (
                <option value="">No lists</option>
              ) : (
                lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.title}
                  </option>
                ))
              )}
            </select>
          </div>

          <div>
            <label htmlFor="create-card-title" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              id="create-card-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="glass-input w-full"
              disabled={isSubmitting}
              required
            />
          </div>

          <div>
            <label htmlFor="create-card-desc" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Description
            </label>
            <textarea
              id="create-card-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="glass-input w-full min-h-[72px] resize-y"
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="create-card-priority" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Priority ({priority})
            </label>
            <input
              id="create-card-priority"
              type="range"
              min={1}
              max={10}
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
              className="w-full accent-accent-400"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="create-card-type" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Card type
            </label>
            <select
              id="create-card-type"
              value={cardType}
              onChange={(e) => setCardType(e.target.value as KanbanCardType)}
              className="glass-input w-full"
              disabled={isSubmitting}
            >
              {KANBAN_CARD_TYPES.map((t) => (
                <option key={t} value={t}>
                  {CARD_TYPE_CONFIG[t].label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="create-card-cos" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Class of service
            </label>
            <select
              id="create-card-cos"
              value={classOfService}
              onChange={(e) => setClassOfService(e.target.value as KanbanClassOfService)}
              className="glass-input w-full"
              disabled={isSubmitting}
            >
              {KANBAN_CLASSES_OF_SERVICE.map((c) => (
                <option key={c} value={c}>
                  {CLASS_OF_SERVICE_CONFIG[c].label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="create-card-due" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Due date
            </label>
            <input
              id="create-card-due"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="glass-input w-full"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="create-card-swim" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Swimlane
            </label>
            <select
              id="create-card-swim"
              value={swimlaneId}
              onChange={(e) => setSwimlaneId(e.target.value)}
              className="glass-input w-full"
              disabled={isSubmitting}
            >
              <option value="">None</option>
              {swimlanes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="create-card-cover" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Cover color
            </label>
            <div className="flex items-center gap-3">
              <input
                id="create-card-cover"
                type="color"
                value={coverColor.startsWith('#') && coverColor.length >= 7 ? coverColor.slice(0, 7) : '#6366f1'}
                onChange={(e) => setCoverColor(e.target.value)}
                className="h-10 w-14 cursor-pointer rounded-lg border border-glass-200/20 bg-transparent p-0.5"
                disabled={isSubmitting}
                aria-label="Cover color"
              />
              <input
                type="text"
                value={coverColor}
                onChange={(e) => setCoverColor(e.target.value)}
                className="glass-input flex-1 font-mono"
                placeholder="#6366f1"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="modal-footer sticky bottom-0 bg-[var(--glass-bg)] pb-1">
            <button type="button" onClick={onClose} className="glass-button-secondary" disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="submit"
              className={cn('btn-primary', isSubmitting && 'opacity-70')}
              disabled={isSubmitting || !listId}
            >
              {isSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : null}
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
